/*
 * Shared shapes for the coding-assignment pages: the assignment's config.json,
 * the page's site-metadata blob, the minibuffer message callback, the
 * test-results map every evaluator's execute() resolves with, and the JSON
 * responses from the runner's endpoints. These are the seams between the entry
 * points (index.ts, snap.ts), the evaluators, and the test-results display
 * modules (testing.ts, generic-testing.ts).
 */

/*
 * The minibuffer message callback (index.ts). `fade` is how many milliseconds
 * until the message fades; omitted (or 0) means it sticks. `clazz` is a CSS
 * class for the minibuffer (e.g. 'error').
 */
export type Message = (text: string, fade?: number, clazz?: string) => void;

/*
 * Thrown by an evaluator that has ALREADY put the failure in front of the
 * student — the minibuffer says "Compile error." and the errors pane has
 * javac's output — so the entry point's catch-all (index.ts reevaluateCode)
 * doesn't paint the raw explanation over the message it just set.
 */
export class ReportedError extends Error {}

/*
 * The assignment's config.json, served alongside the page (see CLAUDE.md
 * "Assignment config.json files"). Open-ended — each handler/evaluator reads
 * the subset it cares about — so unknown keys are allowed through the index
 * signature.
 */
export type AssignmentConfig = {
  language?: string;
  handler?: string;
  interactive?: boolean;
  files?: string[];
  allowNewFiles?: boolean;
  // Harness scripts (run-tests.js and friends) loaded before the student code.
  extraFiles?: string[];
  // Data files fetched and provided to interactive programs.
  dataFiles?: string[];
  // Interactive Java: child JVM -Xmx (clamped server side).
  heapMb?: number;
  // Interactive pages: false drops the REPL tab (JShell on Java pages, the
  // Python REPL on Python pages). Absent means the tab is there.
  replTab?: boolean;
  // Python evaluator: per-run time limit in millis.
  timeout?: number;
  // Id of the repl div (javascript-evaluator).
  repl?: string;
  // Attributes for the evaluation iframe / injected script tags.
  iframe?: Record<string, string | boolean>;
  script?: Record<string, string>;
  // Element ids for the run streams (server-evaluator).
  stdin?: string;
  stdout?: string;
  stderr?: string;
  // Runner-backed grading (server-evaluator).
  server?: {
    testClass?: string;
    run?: boolean;
    canvas?: boolean;
    graphic?: boolean;
  };
  [key: string]: unknown;
};

/*
 * The site-metadata blob the entry points read off the page and pass to every
 * evaluator. '' is a valid serverUrl meaning same-origin (the dev-mode runner
 * proxy); only null/undefined mean there is no runner at all.
 */
export type SiteMetadata = {
  serverUrl?: string | null;
  githubOrganization?: string;
  [key: string]: unknown;
};

/*
 * One test case's outcome. The harnesses produce richer records — the
 * unit-test harness adds args/got/expected/exception, the generic harness
 * label/got/expected — but everything that handles the results *map* (the
 * evaluators, the pill verdicts) needs only `passed`; the display modules
 * assert the richer shapes they render.
 */
export type TestCaseResult = {
  passed: boolean;
  [key: string]: unknown;
};

/*
 * What an evaluator's execute() resolves with and the display modules render:
 * per-function test cases, or null/absent for a function with no results
 * (not defined, wrong signature, ...).
 */
export type TestResults = Record<string, TestCaseResult[] | null | undefined>;

/*
 * The JSON payload from the runner's endpoints (Server.java, reached via the
 * web app's submission proxy or the dev runner proxy): /api/submissions/,
 * /api/run/, /api/canvas/, /api/graphics/. `status` is always present; the
 * rest varies by endpoint and outcome.
 */
export type RunnerResponse = {
  status: number;
  results?: TestResults;
  explanation?: string;
  errorType?: string; // 'compile' or 'runtime'
  stdout?: string;
  stderr?: string;
  code?: string; // /api/canvas/: the generated JS drawing program
  image?: string; // /api/graphics/: the rendered image
};
